/*
  spx-catalog-cards.js — card interactions for snippets/sp-catalog-card.liquid
  (CATALOG_REBUILD_PLAN §2/§4). Vanilla ES5, IIFE, no jQuery.

  Owns: metal-dot variant swap on CLICK/TAP (primary+hover image, price, compare,
  save, hrefs, active state, RTS badge — commits, works on every device); desktop
  HOVER-PREVIEW of a dot's photo+price that reverts on mouse-out (non-committal,
  gated to real hover pointers, debounced); dot-hover metal-name + image preload;
  RTS ship-by date fill via window.SP_FF, shortlist/analytics hook via a
  bubbling 'sp-card:variant' CustomEvent.

  All listeners are DELEGATED on document, so cards appended or swapped in by
  the spx-catalog.js engine / spx-catalog-scroll.js need NO re-init. A
  'collection:reloaded' listener + window.spxCatalogCards.refresh(root) exist
  only to (re)fill RTS ship-by dates on fresh markup.
*/
(function () {
  'use strict';
  if (window.spxCatalogCards) { return; }

  /* ---------- helpers ---------- */

  function hasClass(el, cls) {
    return !!(el && el.nodeType === 1 && el.classList && el.classList.contains(cls));
  }

  function closestByClass(el, cls) {
    while (el && el.nodeType === 1) {
      if (hasClass(el, cls)) { return el; }
      el = el.parentNode;
    }
    return null;
  }

  function fire(el, name, detail) {
    var ev;
    try {
      ev = new CustomEvent(name, { bubbles: true, cancelable: false, detail: detail });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, true, false, detail);
    }
    el.dispatchEvent(ev);
  }

  function norm(t) { return String(t == null ? '' : t).trim().toLowerCase(); }

  function isRTS(t) {
    t = norm(t);
    return t === 'ready to ship' || t === 'rts';
  }

  /* Per-card image map: { "<variantId>": ["<primary>", "<hover>", ...], ... } */
  function getMap(card) {
    if (card.__spImgs) { return card.__spImgs; }
    var map = {};
    var s = card.querySelector('script.sp-card-images');
    if (s) {
      try { map = JSON.parse(s.textContent || s.innerHTML) || {}; } catch (e) { map = {}; }
    }
    card.__spImgs = map;
    return map;
  }

  /* Map URLs are single-size — drop srcset so the browser honors the new src. */
  function setImg(img, url) {
    if (!img || !url) { return; }
    img.removeAttribute('srcset');
    img.src = url;
  }

  /* 2026-08-17 hover-spin: the hover slot can hold an img OR a muted looping video
     (spin files in variant_images[1]). Returns an element of the wanted type, replacing
     the other type in place when a metal swap crosses media kinds. */
  function ensureHoverEl(card, wantVideo) {
    var hover = card.querySelector('.sp-card__img--hover');
    var isVid = !!(hover && hover.tagName === 'VIDEO');
    if (hover && isVid === !!wantVideo) { return hover; }
    var link = card.querySelector('a.sp-card__link');
    if (!link) { return null; }
    var main = link.querySelector('.sp-card__img');
    var el;
    if (wantVideo) {
      el = document.createElement('video');
      el.muted = true; el.loop = true; el.playsInline = true;
      el.setAttribute('muted', ''); el.setAttribute('playsinline', '');
      el.preload = 'none';
      el.tabIndex = -1;
    } else {
      el = document.createElement('img');
      el.alt = '';
      el.setAttribute('loading', 'lazy');
    }
    el.className = 'sp-card__img sp-card__img--hover';
    el.setAttribute('aria-hidden', 'true');
    el.width = (main && main.width) || 600;
    el.height = (main && main.height) || 600;
    if (hover) {
      if (isVid) { try { hover.pause(); } catch (e) {} }
      hover.parentNode.replaceChild(el, hover);
    } else {
      link.appendChild(el);
    }
    return el;
  }

  /* ---------- RTS ship-by date (values come ONLY from window.SP_FF) ---------- */

  function fillRtsDate(rts) {
    if (!rts || rts.hidden || !window.SP_FF) { return; }
    var dateEl = rts.querySelector('[data-sp-rts-date]');
    if (!dateEl) { return; }
    try {
      var ff = rts.getAttribute('data-ff');
      dateEl.textContent = ' · by ' + window.SP_FF.format(window.SP_FF.shipByDate(ff));
    } catch (e) { /* SP_FF absent/changed — badge label alone still renders */ }
  }

  function refresh(root) {
    root = root || document;
    var badges = root.querySelectorAll ? root.querySelectorAll('.sp-card__rts') : [];
    for (var i = 0; i < badges.length; i++) { fillRtsDate(badges[i]); }
    /* mobile: pre-render the swipe hint dots so shoppers see there are more angles */
    if (isTouchLayout()) {
      var cards = root.querySelectorAll ? root.querySelectorAll('.sp-card') : [];
      for (var c = 0; c < cards.length; c++) {
        if (cards[c].__spSwipeIdx == null) { cards[c].__spSwipeIdx = 0; }
        paintSwipeDots(cards[c], cards[c].__spSwipeIdx || 0);
      }
    }
  }

  /* ---------- variant swap ---------- */

  function applyVariant(card, dot) {
    var vid = dot.getAttribute('data-variant-id');
    if (!vid) { return; }
    var map = getMap(card);
    var arr = map[vid] || [];
    var link = card.querySelector('a.sp-card__link');

    /* images */
    var main = card.querySelector('.sp-card__img:not(.sp-card__img--hover)');
    var mainUrl = arr[0] || dot.getAttribute('data-image') || '';
    if (mainUrl) { setImg(main, mainUrl); }
    var hoverUrl = arr[1] || '';
    if (hoverUrl) {
      var wantVid = isVideoUrl(hoverUrl);
      var hover = ensureHoverEl(card, wantVid);
      if (hover) {
        if (wantVid) {
          if (hover.getAttribute('src') !== hoverUrl) { hover.setAttribute('src', hoverUrl); }
        } else {
          setImg(hover, hoverUrl);
        }
        hover.hidden = false;
      }
      if (link) { link.classList.add('has-hover'); }
    } else {
      var oldHover = card.querySelector('.sp-card__img--hover');
      if (oldHover) { oldHover.hidden = true; }
      if (link) { link.classList.remove('has-hover'); }
    }

    /* price / compare / save (server-formatted money strings off the dot) */
    var priceEl = card.querySelector('[data-sp-price]');
    if (priceEl) { priceEl.textContent = dot.getAttribute('data-price') || ''; }
    var cmpEl = card.querySelector('[data-sp-compare]');
    if (cmpEl) {
      var cmpVal = dot.getAttribute('data-compare-price') || '';
      cmpEl.textContent = cmpVal;
      cmpEl.hidden = !cmpVal;
    }
    var saveEl = card.querySelector('[data-sp-save]');
    if (saveEl) {
      var saveVal = dot.getAttribute('data-save') || '';
      saveEl.innerHTML = saveVal;
      saveEl.hidden = !saveVal;
    }

    /* links */
    var url = dot.getAttribute('data-url');
    if (url) {
      var links = card.querySelectorAll('a.sp-card__link, a.sp-card__title-link');
      for (var i = 0; i < links.length; i++) { links[i].setAttribute('href', url); }
    }

    /* active state */
    var dots = card.querySelectorAll('.sp-card__dot');
    for (var j = 0; j < dots.length; j++) {
      dots[j].classList.remove('is-active');
      dots[j].setAttribute('aria-pressed', 'false');
    }
    dot.classList.add('is-active');
    dot.setAttribute('aria-pressed', 'true');
    card.setAttribute('data-variant-id', vid);

    /* RTS badge follows the variant */
    var rts = card.querySelector('.sp-card__rts');
    if (rts) {
      var ff = dot.getAttribute('data-ff') || '';
      rts.setAttribute('data-ff', ff);
      var show = isRTS(ff);
      rts.hidden = !show;
      if (show) { fillRtsDate(rts); }
    }

    /* mobile swipe: new metal = new angle set → back to the first angle */
    card.__spSwipeIdx = 0;
    paintSwipeDots(card, 0);

    /* shortlist / analytics hook */
    fire(card, 'sp-card:variant', {
      productId: card.getAttribute('data-product-id'),
      variantId: vid,
      option1: dot.getAttribute('data-option1') || ''
    });
  }

  /* ---------- desktop hover-preview (non-committal) ----------
     Hovering a metal dot PREVIEWS that metal's photo + price (like Brilliant Earth),
     reverting on mouse-out. It never commits — no active-dot change, no link/analytics
     change; only a CLICK commits (applyVariant). Gated to real hover pointers so touch
     devices (where "hover" fires on tap) never get a stuck preview. Debounced so a fast
     mouse-pass across dots doesn't strobe images/prices. */
  var HOVER_IN_MS = 70, HOVER_OUT_MS = 90;
  var hoverMQ = (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)')) || null;
  var previewing = null; /* the one card currently showing a preview */

  function canHoverPreview() { return hoverMQ ? hoverMQ.matches : false; }

  function snapshotCommitted(card) {
    var main = card.querySelector('.sp-card__img:not(.sp-card__img--hover)');
    var priceEl = card.querySelector('[data-sp-price]');
    var cmpEl = card.querySelector('[data-sp-compare]');
    var saveEl = card.querySelector('[data-sp-save]');
    return {
      src: main ? main.getAttribute('src') : null,
      srcset: main ? main.getAttribute('srcset') : null,
      price: priceEl ? priceEl.textContent : null,
      cmpText: cmpEl ? cmpEl.textContent : null,
      cmpHidden: cmpEl ? cmpEl.hidden : null,
      saveHTML: saveEl ? saveEl.innerHTML : null,
      saveHidden: saveEl ? saveEl.hidden : null
    };
  }

  function restoreSnapshot(card, base) {
    var main = card.querySelector('.sp-card__img:not(.sp-card__img--hover)');
    if (main) {
      if (base.srcset != null) { main.setAttribute('srcset', base.srcset); } else { main.removeAttribute('srcset'); }
      if (base.src != null) { main.setAttribute('src', base.src); }
    }
    var priceEl = card.querySelector('[data-sp-price]');
    if (priceEl && base.price != null) { priceEl.textContent = base.price; }
    var cmpEl = card.querySelector('[data-sp-compare]');
    if (cmpEl) { cmpEl.textContent = base.cmpText || ''; cmpEl.hidden = !!base.cmpHidden; }
    var saveEl = card.querySelector('[data-sp-save]');
    if (saveEl) { saveEl.innerHTML = base.saveHTML || ''; saveEl.hidden = !!base.saveHidden; }
  }

  /* visual-only swap (image + price/compare/save); NO active state, links or events */
  function previewVisual(card, dot) {
    var arr = getMap(card)[dot.getAttribute('data-variant-id')] || [];
    var main = card.querySelector('.sp-card__img:not(.sp-card__img--hover)');
    var mainUrl = arr[0] || dot.getAttribute('data-image') || '';
    if (mainUrl) { setImg(main, mainUrl); }
    var priceEl = card.querySelector('[data-sp-price]');
    if (priceEl) { priceEl.textContent = dot.getAttribute('data-price') || priceEl.textContent; }
    var cmpEl = card.querySelector('[data-sp-compare]');
    if (cmpEl) { var c = dot.getAttribute('data-compare-price') || ''; cmpEl.textContent = c; cmpEl.hidden = !c; }
    var saveEl = card.querySelector('[data-sp-save]');
    if (saveEl) { var sv = dot.getAttribute('data-save') || ''; saveEl.innerHTML = sv; saveEl.hidden = !sv; }
  }

  function beginPreview(card, dot) {
    if (!canHoverPreview()) { return; }
    if (dot.classList.contains('is-active')) { return; } /* already the committed metal */
    if (previewing && previewing !== card) { endPreview(previewing); }
    if (card.__spBase == null) { card.__spBase = snapshotCommitted(card); }
    previewing = card;
    previewVisual(card, dot);
  }

  function endPreview(card) {
    if (!card) { return; }
    if (card.__spBase != null) { restoreSnapshot(card, card.__spBase); card.__spBase = null; }
    if (previewing === card) { previewing = null; }
  }

  function clearPreviewState(card) {
    if (card.__spInT) { clearTimeout(card.__spInT); card.__spInT = null; }
    if (card.__spOutT) { clearTimeout(card.__spOutT); card.__spOutT = null; }
    card.__spBase = null;
    if (previewing === card) { previewing = null; }
  }

  /* ---------- delegated events ---------- */

  document.addEventListener('click', function (e) {
    var dot = closestByClass(e.target, 'sp-card__dot');
    if (!dot) { return; }
    var card = closestByClass(dot, 'sp-card');
    if (!card) { return; }
    e.preventDefault();
    clearPreviewState(card); /* a click commits — drop any pending hover-preview/snapshot */
    applyVariant(card, dot);
  }, false);

  document.addEventListener('mouseover', function (e) {
    var dot = closestByClass(e.target, 'sp-card__dot');
    if (!dot) { return; }
    if (e.relatedTarget && dot.contains(e.relatedTarget)) { return; }
    var card = closestByClass(dot, 'sp-card');
    if (!card) { return; }
    /* ported behavior: swatch mouseenter writes " (14K Rose Gold)" into the title */
    var span = card.querySelector('.sp-metal-hover');
    if (span) { span.textContent = ' (' + (dot.getAttribute('data-option1') || '') + ')'; }
    /* preload primary + hover for the hovered metal */
    var arr = getMap(card)[dot.getAttribute('data-variant-id')] || [];
    for (var i = 0; i < 2; i++) {
      if (arr[i] && !isVideoUrl(arr[i])) { var im = new Image(); im.src = arr[i]; }
    }
    /* debounced desktop preview (cancels any pending revert on this card) */
    if (card.__spOutT) { clearTimeout(card.__spOutT); card.__spOutT = null; }
    if (card.__spInT) { clearTimeout(card.__spInT); }
    card.__spInT = setTimeout(function () { card.__spInT = null; beginPreview(card, dot); }, HOVER_IN_MS);
  }, false);

  document.addEventListener('mouseout', function (e) {
    var dot = closestByClass(e.target, 'sp-card__dot');
    if (!dot) { return; }
    if (e.relatedTarget && dot.contains(e.relatedTarget)) { return; }
    var card = closestByClass(dot, 'sp-card');
    if (!card) { return; }
    var span = card.querySelector('.sp-metal-hover');
    if (span) { span.textContent = ''; }
    /* debounced revert — cancelled if the pointer lands on another dot of this card */
    if (card.__spInT) { clearTimeout(card.__spInT); card.__spInT = null; }
    if (card.__spOutT) { clearTimeout(card.__spOutT); }
    card.__spOutT = setTimeout(function () { card.__spOutT = null; endPreview(card); }, HOVER_OUT_MS);
  }, false);

  /* ---------- hover-spin: play the video hover while the pointer is on the card ----------
     (2026-08-17) The hover reveal itself is CSS (.has-hover:hover). This only starts and
     stops playback; preload="none" keeps 30-card grids from downloading 1.7MB spins upfront. */
  document.addEventListener('mouseover', function (e) {
    var link = closestByClass(e.target, 'sp-card__link');
    if (!link || !hasClass(link, 'has-hover')) { return; }
    if (e.relatedTarget && link.contains(e.relatedTarget)) { return; }
    var v = link.querySelector('video.sp-card__img--hover');
    if (!v || v.hidden) { return; }
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { return; }
    var p = v.play();
    if (p && p.catch) { p.catch(function () {}); }
  }, false);

  document.addEventListener('mouseout', function (e) {
    var link = closestByClass(e.target, 'sp-card__link');
    if (!link) { return; }
    if (e.relatedTarget && link.contains(e.relatedTarget)) { return; }
    var v = link.querySelector('video.sp-card__img--hover');
    if (!v || v.paused) { return; }
    v.pause();
    try { v.currentTime = 0; } catch (err) {}
  }, false);

  /* ---------- mobile swipe: flip a variant's angle photos ----------
     Touch devices get no hover-preview, so swiping the card image left/right
     cycles the current variant's angles (top → profile → front → …). The change
     lands on release with a soft cross-fade; a horizontal swipe suppresses the
     card's link tap while vertical scroll is never blocked. Small dots hint that
     more angles exist. Desktop (hover) is untouched. */
  var SWIPE_MIN = 35;
  function isVideoUrl(u) { return /\.(mp4|webm|m3u8)(\?|#|$)/i.test(u || ''); }

  function swipeImages(card) {
    var vid = card.getAttribute('data-variant-id');
    var arr = (vid && getMap(card)[vid]) || [];
    /* Swipe is EXACTLY the front photo + its hover/second image — arr[0] and
       arr[1] only, mirroring the desktop hover-preview. NOT the full angle set
       (owner 2026-07-09: "swiping through all the settings' photos makes no
       sense"). If the hover slot just duplicates the front, dedupe leaves one
       image → no swipe, no dots. */
    var out = [], seen = {};
    for (var i = 0; i < arr.length && i < 2; i++) {
      var u = arr[i];
      if (!u || isVideoUrl(u)) { continue; }
      var key = u.split('/').pop().split('?')[0].toLowerCase();
      if (seen[key]) { continue; }
      seen[key] = 1;
      out.push(u);
    }
    if (!out.length) {
      var m0 = card.querySelector('.sp-card__img:not(.sp-card__img--hover)');
      if (m0 && m0.getAttribute('src')) { out.push(m0.getAttribute('src')); }
    }
    return out;
  }

  function ensureSwipeDots(card, n) {
    var media = card.querySelector('.sp-card__media');
    if (!media) { return null; }
    var dots = media.querySelector('.sp-card__swipe-dots');
    if (n < 2) { if (dots && dots.parentNode) { dots.parentNode.removeChild(dots); } return null; }
    if (!dots) {
      dots = document.createElement('div');
      dots.className = 'sp-card__swipe-dots';
      dots.setAttribute('aria-hidden', 'true');
      media.appendChild(dots);
    }
    if (dots.childElementCount !== n) {
      dots.innerHTML = '';
      for (var i = 0; i < n; i++) { dots.appendChild(document.createElement('span')); }
    }
    return dots;
  }

  function paintSwipeDots(card, idx) {
    var dots = ensureSwipeDots(card, swipeImages(card).length);
    if (!dots) { return; }
    var kids = dots.children;
    for (var i = 0; i < kids.length; i++) { kids[i].className = (i === idx) ? 'is-on' : ''; }
  }

  /* Slide (not fade): the target photo slides in from the swipe direction while
     the current one slides out — a smooth carousel "next" feel. dir < 0 (swipe
     left) pulls the next image in from the right. */
  function showSwipeImage(card, idx, dir) {
    if (card.__spSliding) { return; }              /* ignore taps mid-animation */
    var imgs = swipeImages(card);
    var n = imgs.length;
    if (n < 2) { return; }
    idx = ((idx % n) + n) % n;
    var prevIdx = card.__spSwipeIdx || 0;
    if (idx === prevIdx) { return; }
    card.__spSwipeIdx = idx;
    paintSwipeDots(card, idx);

    var link = card.querySelector('a.sp-card__link');
    var main = card.querySelector('.sp-card__img:not(.sp-card__img--hover)');
    if (!link || !main) { return; }

    /* respect reduced-motion — swap instantly, no slide */
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setImg(main, imgs[idx]);
      return;
    }

    var d = (dir < 0) ? 1 : -1;                    /* incoming from right on "next" */
    var EASE = 'transform 0.34s cubic-bezier(0.22, 0.61, 0.36, 1)';
    var incoming = document.createElement('img');
    incoming.className = 'sp-card__img sp-card__img--slide';
    incoming.setAttribute('alt', '');
    incoming.setAttribute('aria-hidden', 'true');
    incoming.src = imgs[idx];
    incoming.style.transition = 'none';
    incoming.style.transform = 'translateX(' + (d * 100) + '%)';
    link.appendChild(incoming);
    card.__spSliding = true;

    void incoming.offsetWidth;                     /* reflow so the start offset sticks */
    incoming.style.transition = EASE;
    main.style.transition = EASE;
    incoming.style.transform = 'translateX(0)';
    main.style.transform = 'translateX(' + (-d * 100) + '%)';

    var settled = false;
    function done() {
      if (settled) { return; }
      settled = true;
      setImg(main, imgs[idx]);                     /* commit target onto the real img */
      main.style.transition = 'none';
      main.style.transform = 'none';
      if (incoming.parentNode) { incoming.parentNode.removeChild(incoming); }
      card.__spSliding = false;
    }
    incoming.addEventListener('transitionend', done);
    setTimeout(done, 440);                          /* fallback if transitionend misfires */
  }

  var touchMQ = (window.matchMedia && window.matchMedia('(hover: none)')) || null;
  function isTouchLayout() { return touchMQ ? touchMQ.matches : ('ontouchstart' in window); }

  var _sw = null; /* active gesture: {card, x, y, moved, horiz} */
  document.addEventListener('touchstart', function (e) {
    _sw = null;
    if (!isTouchLayout() || !e.touches || e.touches.length !== 1) { return; }
    var media = closestByClass(e.target, 'sp-card__media');
    if (!media) { return; }
    var card = closestByClass(media, 'sp-card');
    if (!card) { return; }
    var imgs = swipeImages(card);
    if (imgs.length < 2) { return; }
    /* warm the other frame so the slide-in is instant (no mobile hover to preload it) */
    if (!card.__spPreloaded) {
      card.__spPreloaded = true;
      for (var pi = 0; pi < imgs.length; pi++) { var pim = new Image(); pim.src = imgs[pi]; }
    }
    var t = e.touches[0];
    _sw = { card: card, x: t.clientX, y: t.clientY, moved: false, horiz: false };
  }, { passive: true });

  document.addEventListener('touchmove', function (e) {
    if (!_sw || !e.touches || !e.touches.length) { return; }
    var t = e.touches[0];
    var dx = t.clientX - _sw.x, dy = t.clientY - _sw.y;
    if (!_sw.moved && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      _sw.moved = true;
      _sw.horiz = Math.abs(dx) > Math.abs(dy);
    }
  }, { passive: true });

  document.addEventListener('touchend', function (e) {
    var s = _sw; _sw = null;
    if (!s || !s.moved || !s.horiz) { return; }
    var ct = (e.changedTouches && e.changedTouches[0]) || null;
    if (!ct) { return; }
    var dx = ct.clientX - s.x;
    if (Math.abs(dx) < SWIPE_MIN) { return; }
    s.card.__spSwiped = true; /* suppress the link tap that fires after */
    showSwipeImage(s.card, (s.card.__spSwipeIdx || 0) + (dx < 0 ? 1 : -1), dx);
    setTimeout(function () { s.card.__spSwiped = false; }, 350);
  }, { passive: true });

  /* a horizontal swipe must NOT open the PDP */
  document.addEventListener('click', function (e) {
    var link = closestByClass(e.target, 'sp-card__link');
    if (!link) { return; }
    var card = closestByClass(link, 'sp-card');
    if (card && card.__spSwiped) { e.preventDefault(); e.stopPropagation(); card.__spSwiped = false; }
  }, true);

  /* re-fill RTS dates after any engine swap; engine dispatches collection:reloaded */
  document.addEventListener('collection:reloaded', function () { refresh(document); }, false);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { refresh(document); }, false);
  } else {
    refresh(document);
  }

  window.spxCatalogCards = {
    refresh: refresh,
    applyVariant: applyVariant
  };
})();
